import { DOCUMENT, DestroyRef, Injectable, Injector, NgZone, PLATFORM_ID, effect, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { RestService } from '../rest/rest.service';
import { SessionService } from '../session/session.service';
import { StorageService } from '../storage/storage.service';

/** A feed younger than this is reused as is when the bell panel opens (no second request). */
export const FEED_FRESCO_MS = 10_000;

/** Poll interval for the unread counter while the tab is visible. */
export const INTERVALO_CAMPANA_MS = 90_000;

/**
 * Unread counter of the toolbar bell (odd/tasks/notificaciones-actividad.md, N1, S2.3).
 *
 * Browser only and only with a session: asks `GET /socialfeed/unread` on start/login, every
 * 90 s while the document is visible (paused when hidden) and after marking read. The API that
 * is in production today has no `/unread`: when it fails, the count is computed from the feed
 * with the old local rule (items newer than `notificationLastDate`), so nothing regresses.
 */
@Injectable({ providedIn: 'root' })
export class NotificacionesCampanaService {
  private session = inject(SessionService);
  private injector = inject(Injector);
  private zone = inject(NgZone);
  private document = inject<Document>(DOCUMENT);
  private esNavegador = isPlatformBrowser(inject(PLATFORM_ID));

  readonly unread = signal(0);

  /**
   * Last good `GET /socialfeed` response. The bell panel paints it at once and refreshes
   * behind it, so opening the panel no longer waits for the API (02/11/2026: rows appeared
   * ~0.4-1.1 s after the click). Cleared on logout so another account never sees it.
   */
  ultimoFeed: any = null;
  private ultimoFeedEn = 0;
  private feedEnCurso: Promise<any> | null = null;

  private temporizador: ReturnType<typeof setInterval> | null = null;
  private escuchandoVisibilidad = false;
  private conSesion = false;

  constructor() {
    if (!this.esNavegador) return;
    inject(DestroyRef).onDestroy(() => this.parar());
    effect(() => {
      const user = this.session.user();
      // Unverified accounts get 403 on /socialfeed and the interceptor turns it into the
      // «valida tu email» modal: never poll for them.
      if (user && user.role !== 'unverified') this.arrancar();
      else this.parar();
    });
  }

  // RestService and StorageService are resolved on first use: they are only needed in the
  // browser with a session, and resolving them lazily keeps the toolbar constructible where
  // there is no HTTP stack (SSR shell, component tests).
  private get rest(): RestService { return this.injector.get(RestService); }
  private get storage(): StorageService { return this.injector.get(StorageService); }

  private arrancar() {
    this.conSesion = true;
    if (!this.escuchandoVisibilidad) {
      this.document.addEventListener('visibilitychange', this.alCambiarVisibilidad);
      this.escuchandoVisibilidad = true;
    }
    void this.refrescar();
    if (!this.document.hidden) this.programar();
  }

  private parar() {
    this.conSesion = false;
    this.desprogramar();
    if (this.escuchandoVisibilidad) {
      this.document.removeEventListener('visibilitychange', this.alCambiarVisibilidad);
      this.escuchandoVisibilidad = false;
    }
    this.unread.set(0);
    this.ultimoFeed = null;
    this.feedEnCurso = null;
  }

  /**
   * `GET /socialfeed`, shared: concurrent callers (hover prefetch, panel open, feed fallback
   * of the counter) get the same request. Resolves the response, or `null` when it fails;
   * only a good response replaces `ultimoFeed`. `maxEdadMs`: a stored feed at most this old
   * is returned without asking again (the click already asked; the panel mounts right after).
   */
  cargarFeed(maxEdadMs = 0): Promise<any> {
    if (!this.esNavegador) return Promise.resolve(null);
    if (this.feedEnCurso) return this.feedEnCurso;
    if (maxEdadMs > 0 && this.ultimoFeed && Date.now() - this.ultimoFeedEn <= maxEdadMs) return Promise.resolve(this.ultimoFeed);
    const peticion = this.feedEnCurso = (async () => {
      try {
        const result: any = await this.rest.getNotifications();
        if (result?.ok && this.feedEnCurso === peticion) {
          this.ultimoFeed = result;
          this.ultimoFeedEn = Date.now();
        }
        return result ?? null;
      } catch (error) {
        console.error(error);
        return null;
      } finally {
        if (this.feedEnCurso === peticion) this.feedEnCurso = null;
      }
    })();
    return peticion;
  }

  /** Warms the feed (bell hovered/focused, idle after start) when there is none yet. */
  precargarFeed(): void {
    if (!this.conSesion || this.ultimoFeed || this.feedEnCurso) return;
    void this.cargarFeed();
  }

  private alCambiarVisibilidad = () => {
    if (!this.conSesion) return;
    if (this.document.hidden) {
      this.desprogramar();
    } else {
      void this.refrescar();
      this.programar();
    }
  };

  // Outside the zone: a live interval inside it would keep the app from ever becoming stable
  // (the service worker registers with `registerWhenStable`).
  private programar() {
    if (this.temporizador) return;
    this.zone.runOutsideAngular(() => {
      this.temporizador = setInterval(() => { void this.refrescar(); }, INTERVALO_CAMPANA_MS);
    });
  }

  private desprogramar() {
    if (!this.temporizador) return;
    clearInterval(this.temporizador);
    this.temporizador = null;
  }

  private fijar(n: number) {
    this.zone.run(() => this.unread.set(Math.max(0, n)));
  }

  /** Asks the API for the counter; falls back to the feed when `/socialfeed/unread` is missing. */
  async refrescar(): Promise<void> {
    if (!this.conSesion) return;
    try {
      const resp: any = await this.rest.getUnreadNotifications();
      if (!this.conSesion) return; // logged out (or torn down) while waiting
      if (resp?.ok && typeof resp.unread === 'number') {
        this.fijar(resp.unread);
        return;
      }
      const n = await this.contarDesdeFeed();
      if (this.conSesion) this.fijar(n);
    } catch (error) {
      console.error(error);
    }
  }

  private async contarDesdeFeed(): Promise<number> {
    const result: any = await this.cargarFeed();
    if (!this.conSesion || !result?.ok) return this.unread();
    if (typeof result.feed?.unread === 'number') return result.feed.unread;
    const items: any[] = result.feed?.results ?? [];
    if (items.some(n => typeof n?.read === 'boolean')) return items.filter(n => n.read === false).length;
    const ultima = await this.storage.getItem('notificationLastDate');
    if (!ultima) return 0;
    const desde = new Date(ultima).getTime();
    return items.filter(n => desde < new Date(n.created_at).getTime()).length;
  }

  /** "Marcar todas como leídas": `POST /socialfeed/read/`, counter to zero. */
  async marcarTodas(): Promise<void> {
    this.fijar(0);
    try {
      await this.rest.markAllNotificationsRead();
      // The fallback rule counts from this date: keep it in step with the server.
      await this.storage.setItem('notificationLastDate', new Date());
    } catch (error) {
      console.error(error);
    }
    await this.refrescar();
  }

  /** One item read: `POST /socialfeed/read/:action/:id`, fire-and-forget. */
  /** `cuantos`: rows sharing this action and id (every like on one deal), all marked at once. */
  marcarUna(action: string, id: number | string, cuantos = 1): void {
    this.fijar(this.unread() - cuantos);
    this.rest.markNotificationRead(action, id)
      .then(() => this.refrescar())
      .catch(error => console.error(error));
  }
}
